import routes from "routes";
import { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import PropTypes from "prop-types";

// MUI
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

// Vision UI
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

import {
  useVisionUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useVisionUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, sidenavColor } = controller;
  const { pathname } = useLocation();

  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    const handleTransparentNavbar = () => {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    };

    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();

    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, true);

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        {/* Bloco esquerdo vazio */}
        <VuiBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })} />

        {/* Navegação central com ícones e nomes */}
        {!isMini && (
          <VuiBox
            display={{ xs: "none", lg: "flex" }}
            justifyContent="center"
            alignItems="center"
            gap={4}
            flexGrow={1}
          >
            {routes
            .filter((route) => route.type === "collapse")
            .map(({ key, name, route: path, icon }) => {
              const isActive = pathname === path;

              return (
                <Link key={key} to={path} style={{ textDecoration: "none" }}>
                  <VuiBox
                    display="flex"
                    alignItems="center"
                    px={2}
                    py={1}
                    borderRadius="lg"
                    sx={({ palette, functions, borders, shadows }) => ({
                      background: isActive ? palette.sidenav.button : "transparent",
                      color: palette.white.main,
                      cursor: "pointer",
                      transition: "all 200ms ease-in-out",
                      boxShadow: isActive ? shadows.xl : "none",
                      "&:hover": {
                        background: palette.sidenav.button,
                      },
                    })}
                  >
                    {/* Ícone com efeito visual igual ao sidenavCollapse */}
                    <VuiBox
                      sx={({ palette, borders, functions, shadows }) => ({
                        backgroundColor: isActive
                          ? palette[sidenavColor]?.main || "#4318ff"
                          : palette.sidenav.button,
                        minWidth: functions.pxToRem(32),
                        minHeight: functions.pxToRem(32),
                        borderRadius: borders.borderRadius.button,
                        display: "grid",
                        placeItems: "center",
                        boxShadow: shadows.md,
                        transition: "all 200ms ease-in-out",
                        "& svg, & svg g": {
                          fill: isActive
                            ? palette.white.main
                            : palette[sidenavColor]?.main || "#4318ff",
                        },
                      })}
                    >
                      {icon}
                    </VuiBox>

                    <VuiTypography
                      variant="button"
                      fontWeight={isActive ? "medium" : "regular"}
                      color="white"
                      sx={{
                        ml: 1.5,
                        textTransform: "none",
                        fontSize: "0.875rem",
                      }}
                    >
                      {name}
                    </VuiTypography>
                  </VuiBox>
                </Link>
              );
            })}

          </VuiBox>
        )}

        {/* Botões da direita */}
        {!isMini && (
          <VuiBox sx={(theme) => navbarRow(theme, { isMini })}>
            <IconButton
              size="small"
              color="inherit"
              sx={navbarMobileMenu}
              onClick={handleMiniSidenav}
            >
              <Icon className="text-white">{miniSidenav ? "menu_open" : "menu"}</Icon>
            </IconButton>

            <IconButton
              size="small"
              color="inherit"
              sx={navbarIconButton}
              onClick={handleConfiguratorOpen}
            >
              <Icon>settings</Icon>
            </IconButton>
          </VuiBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
